// src/features/inventory/pages/ItemDetailPage/tabs/BatchNumbers/index.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, CompareArrows as TransferIcon, SwapVert as AdjustIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useInventoryLotPermissions, useInventoryStockPermissions } from '../../../../../auth/hooks/usePermissions';
import { useLots } from '../../../../hooks/useLots';
import AddBatchDialog from './AddBatchDialog';
import { withInfo } from '../../../../../../components/common/InfoTip';
import RecordTransactionDialog from '../../RecordTransactionDialog';
import TransferStockDialog from '../../TransferStockDialog';
import { LOT_STATUS_COLOR } from '../../../../constants/lotStatuses';

export default function BatchNumbers({ itemId, uom, refreshKey, onStockChanged }) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { canCreate } = useInventoryLotPermissions();
  const { canRecord } = useInventoryStockPermissions();
  const { lots, loading, error, clearError, fetchLotsByItem } = useLots();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adjustLot, setAdjustLot] = useState(null);
  const [transferLot, setTransferLot] = useState(null);

  useEffect(() => {
    fetchLotsByItem(itemId);
  }, [fetchLotsByItem, itemId, refreshKey]);

  // The page refreshes the item, whose new refreshKey refetches the lots here
  const stockChanged = () => (onStockChanged ? onStockChanged() : fetchLotsByItem(itemId));

  // Collect unique locations across all lots for dynamic columns
  const locationColumns = useMemo(() => {
    const seen = new Map();
    for (const lot of lots) {
      for (const b of (lot.location_balances ?? [])) {
        if (!seen.has(b.location_id)) {
          seen.set(b.location_id, b.location_id == null ? t('inventory:items.no_location') : b.location_name);
        }
      }
    }
    return Array.from(seen.entries())
      .map(([locId, locName]) => ({ locId, locName }))
      .sort((a, b) => (a.locId == null ? -1 : b.locId == null ? 1 : 0));
  }, [lots, t]);

  const columns = [
    {
      field: 'lot_number',
      headerName: t('inventory:items.tabs.batches.columns.lot_number'),
      flex: 1.2,
      minWidth: 170,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{value}</Typography>
      ),
    },
    withInfo({
      field: 'status',
      headerName: t('inventory:items.tabs.batches.columns.status'),
      flex: 0.9,
      renderCell: ({ value }) => (
        <Chip
          label={t(`inventory:items.tabs.batches.status.${value.toLowerCase()}`)}
          color={LOT_STATUS_COLOR[value.toLowerCase()] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    }, t('inventory:help.batches.status')),
    withInfo({
      field: 'quantity_on_hand',
      headerName: locationColumns.length > 0
        ? t('inventory:items.tabs.batches.columns.total')
        : t('inventory:items.tabs.batches.columns.qty_on_hand'),
      width: 120,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight="medium">{value}</Typography>
      ),
    }, t('inventory:help.batches.total')),
    ...locationColumns.map(({ locId, locName }) => withInfo({
      field: `loc_${locId ?? 'unassigned'}`,
      headerName: locName,
      width: 140,
      sortable: false,
      disableColumnMenu: true,
      // Negative shows too (taken out here without a location), so the columns add up to the total
      renderCell: ({ row }) => {
        const b = (row.location_balances ?? []).find(b => b.location_id === locId);
        const qty = b?.quantity ?? 0;
        return qty !== 0
          ? <Typography variant="body2" color={qty < 0 ? 'error.main' : undefined}>{qty}</Typography>
          : <Typography variant="body2" color="text.secondary">-</Typography>;
      },
    }, t(locId == null ? 'inventory:help.stock.no_location' : 'inventory:help.batches.location'))),
    withInfo({
      field: 'expiry_date',
      headerName: t('inventory:items.tabs.batches.columns.expiry_date'),
      flex: 1,
      renderCell: ({ value }) => value
        ? <Typography variant="body2">{new Date(value).toLocaleDateString()}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    }, t('inventory:help.batches.expiry_date')),
    ...(canRecord ? [{
      field: 'actions',
      headerName: '',
      width: 96,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('inventory:items.transfer_stock.button')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setTransferLot(row); }}>
              <TransferIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('inventory:items.tabs.batches.adjust_stock.button_tooltip')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAdjustLot(row); }}>
              <AdjustIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }] : []),
  ];

  if (loading && lots.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flex="1" minHeight="0" flexDirection="column">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>
      )}

      {canCreate && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            {t('inventory:items.tabs.batches.dialog.open_button')}
          </Button>
        </Box>
      )}

      {lots.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('inventory:items.tabs.batches.empty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: "hidden" }}>
          <DataGrid
            rows={lots}
            columns={columns}
            getRowId={(row) => row.lot_id}
            onRowClick={(params) => navigate(`/inventory/batches/${params.row.lot_id}`)}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{
              flex: 1,
              border: 'none',
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': { alignItems: 'center' },
            }}
          />
        </Box>
      )}

      <AddBatchDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={stockChanged}
        itemId={itemId}
        uom={uom}
      />

      <RecordTransactionDialog
        open={Boolean(adjustLot)}
        onClose={() => setAdjustLot(null)}
        onSuccess={() => { stockChanged(); setAdjustLot(null); }}
        lot={adjustLot}
        itemId={itemId}
        uom={uom}
      />

      <TransferStockDialog
        open={Boolean(transferLot)}
        onClose={() => setTransferLot(null)}
        onSuccess={() => { stockChanged(); setTransferLot(null); }}
        itemId={itemId}
        uom={uom}
        preselectedLot={transferLot}
      />
    </Box>
  );
}
